import { useState } from "react";
import { AMCS, IMG } from "@/data/demo";
import { PageHeader, Card, Btn, Icon, DemoBadge } from "@/components/kit";
import { HotBadge, LivePulse, SocialProof, Countdown, LiveNudge, hotFor } from "@/components/Engagement";
import { toast } from "sonner";

const RES_ICON = { PDF: "FileText", PPT: "Presentation", Forms: "ClipboardList", Images: "Image", Tool: "Wrench" };

export default function AMCs() {
  const [following, setFollowing] = useState({});
  const [selected, setSelected] = useState(null);

  return (
    <div className="fade-up">
      <PageHeader title="AMC Hub" subtitle="Rich AMC profiles — factsheets, latest communication, webinars & distributor resources." icon="Building2" color="#3B82F6" />

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-5" data-testid="amcs-live-strip">
        <LivePulse label="14 MFDs following AMC updates now" />
        <SocialProof icon="Video">Live webinars this week</SocialProof>
        <SocialProof icon="Rocket">New NFOs closing soon</SocialProof>
      </div>

      <Card className="mb-6 bg-[#0A0A0A] text-white border-0 relative overflow-hidden">
        <img src={IMG.skyline[1]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2"><Icon name="Quote" size={18} className="text-blue-300" /><span className="text-xs uppercase tracking-widest font-bold text-gray-400">AMC View of the Day</span><DemoBadge /></div>
          <p className="text-lg font-medium">"We stay constructive on large-caps and are selectively adding duration in debt as the rate cycle matures. Volatility is an opportunity to build SIP discipline."</p>
          <div className="text-sm text-gray-400 mt-3">— Chief Investment Officer, Zenith Mutual Fund <span className="inline-flex items-center gap-0.5 text-blue-400 ml-1"><Icon name="BadgeCheck" size={13} /> AMC Verified</span></div>
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 gap-5">
        {AMCS.map((a) => (
          <div key={a.name} onClick={() => setSelected(a)} className="card-mfx overflow-hidden hover-lift cursor-pointer" data-testid="amc-card">
            <div className="h-24 relative overflow-hidden">
              <img src={a.banner} alt={a.name} className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0" style={{ background: `linear-gradient(120deg, ${a.color}bb, ${a.color}44)` }} />
              <div className="absolute -bottom-6 left-5 w-14 h-14 rounded-2xl grid place-items-center text-white font-head font-bold text-xl shadow-lg ring-4 ring-white" style={{ background: a.color }}>{a.name[0]}</div>
            </div>
            <div className="p-6 pt-8">
              <div className="flex items-center gap-3">
                <div>
                  <div className="flex items-center gap-1.5"><h3 className="font-head font-semibold">{a.name}</h3>{a.verified && <Icon name="BadgeCheck" size={15} className="text-blue-500" />}</div>
                  <div className="text-xs text-gray-400">AUM {a.aum} · {a.schemes} schemes</div>
                </div>
                <Btn variant={following[a.name] ? "outline" : "soft"} className="ml-auto" data-testid="amc-follow-btn"
                  onClick={(e) => { e.stopPropagation(); setFollowing({ ...following, [a.name]: !following[a.name] }); toast.success(following[a.name] ? "Unfollowed" : `Following ${a.name}`); }}>
                  {following[a.name] ? "Following" : "Follow"}
                </Btn>
              </div>
              <div className="mt-4 rounded-xl bg-gray-50 p-4"><div className="text-xs font-bold text-gray-500 uppercase mb-1">Today's view</div><p className="text-sm text-gray-700">{a.view}</p></div>
              <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                {[["Factsheets", a.factsheets.length, "FileText"], ["Updates", a.communication.length, "Megaphone"], ["Webinars", a.webinars.length, "Video"], ["Resources", a.resources.length, "FolderOpen"]].map(([l, n, ic]) => (
                  <div key={l} className="rounded-xl bg-gray-50 py-2"><Icon name={ic} size={15} className="mx-auto text-gray-400" /><div className="font-head font-bold text-sm mt-0.5">{n}</div><div className="text-[10px] text-gray-400">{l}</div></div>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <HotBadge badge={hotFor(a.name).badge} />
                <SocialProof icon="Users">{200 + hotFor(a.name).weekViews} MFDs follow</SocialProof>
              </div>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-400 mt-6 flex items-center gap-2"><Icon name="Info" size={13} /> AMC names, AUM, factsheets and resources are fictional demo data.</p>

      {selected && (
        <div className="fixed inset-0 z-[95] bg-black/50 backdrop-blur-sm grid place-items-start justify-center p-4 overflow-y-auto" onClick={() => setSelected(null)}>
          <div className="w-full max-w-3xl card-mfx overflow-hidden my-6" onClick={(e) => e.stopPropagation()} data-testid="amc-room">
            <div className="h-40 relative">
              <img src={selected.banner} alt={selected.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: `linear-gradient(120deg, ${selected.color}dd, ${selected.color}66)` }} />
              <button onClick={() => setSelected(null)} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 grid place-items-center" data-testid="amc-room-close"><Icon name="X" size={18} /></button>
              <div className="absolute bottom-4 left-6 text-white">
                <div className="flex items-center gap-2"><h2 className="font-head text-2xl font-bold">{selected.name}</h2>{selected.verified && <Icon name="BadgeCheck" size={20} />}</div>
                <div className="text-sm text-white/90">AUM {selected.aum} · {selected.schemes} schemes</div>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <p className="text-sm text-gray-600">{selected.about}</p>
              <div className="rounded-xl bg-gray-50 p-4"><div className="text-xs font-bold text-gray-500 uppercase mb-1">Today's market view</div><p className="text-sm text-gray-700">{selected.view}</p></div>

              <Section title="Factsheets" icon="FileText">
                {selected.factsheets.map((f) => (
                  <div key={f.fund} className="flex items-center gap-2 rounded-xl border border-gray-100 p-3 text-sm">
                    <Icon name="FileText" size={15} className="text-blue-500" />{f.fund}
                    <span className="text-[11px] text-gray-400 ml-1">{f.period}</span>
                    <Btn variant="soft" className="ml-auto !py-1.5" data-testid="amc-factsheet-dl" onClick={() => toast.success("Downloading factsheet (demo)")}><Icon name="Download" size={13} /> Factsheet</Btn>
                  </div>
                ))}
              </Section>

              <Section title="Latest communication" icon="Megaphone">
                {selected.communication.map((c) => (
                  <div key={c.title} className="flex items-start gap-2 rounded-xl border border-gray-100 p-3 text-sm">
                    <Icon name="Mail" size={15} className="text-orange-500 mt-0.5 shrink-0" />
                    <div><div className="font-medium">{c.title}</div><div className="text-[11px] text-gray-400">{c.date}</div></div>
                  </div>
                ))}
              </Section>

              <Section title="Latest NFOs" icon="Rocket">
                {selected.nfos.map((n) => <div key={n.name} className="flex items-center gap-2 rounded-xl border border-gray-100 p-3 text-sm"><Icon name="Rocket" size={15} className="text-orange-500" />{n.name}<span className="ml-auto text-[11px] bg-orange-50 text-orange-600 rounded-full px-2 py-0.5 font-semibold">{n.status}</span></div>)}
              </Section>

              <Section title="Webinars" icon="Video">
                {selected.webinars.map((w) => <div key={w.title} className="flex items-center gap-2 rounded-xl border border-gray-100 p-3 text-sm"><Icon name="PlayCircle" size={15} className="text-blue-500" /><div className="min-w-0"><div>{w.title}</div><div className="text-[11px] text-gray-400 flex items-center gap-2 flex-wrap">{w.date}<Countdown seed={w.title} hours={96} label="Starts in" /></div></div><Btn variant="soft" className="ml-auto !py-1.5" onClick={() => toast.success("Registered (demo)")}>Register</Btn></div>)}
              </Section>

              <Section title="Distributor resources" icon="FolderOpen">
                <div className="grid sm:grid-cols-2 gap-2">
                  {selected.resources.map((r) => (
                    <button key={r.name} onClick={() => toast.success("Downloading (demo)")} data-testid="amc-resource-dl" className="flex items-center gap-2 rounded-xl border border-gray-100 p-3 text-sm text-left hover:bg-gray-50">
                      <Icon name={RES_ICON[r.type] || "File"} size={15} className="text-violet-500 shrink-0" />
                      <span className="min-w-0 truncate">{r.name}</span>
                      <span className="ml-auto text-[10px] bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">{r.type}</span>
                    </button>
                  ))}
                </div>
              </Section>

              <Section title="Fund managers" icon="Users">
                {selected.managers.map((m) => <div key={m.n} className="flex items-center gap-3 rounded-xl border border-gray-100 p-3"><div className="w-9 h-9 rounded-full grad-violet-coral grid place-items-center text-white text-sm font-bold">{m.n[0]}</div><div><div className="text-sm font-medium">{m.n}</div><div className="text-xs text-gray-400">{m.r}</div></div></div>)}
              </Section>

              <Btn className="w-full justify-center" data-testid="amc-room-follow" onClick={() => { setFollowing((f) => ({ ...f, [selected.name]: true })); toast.success(`Following ${selected.name}`); }}><Icon name="Plus" size={15} /> Follow this AMC</Btn>
            </div>
          </div>
        </div>
      )}

      <LiveNudge id="amcs" title="Don't miss AMC webinars" message="Live sessions and NFO windows close fast. Follow an AMC to stay ahead." ctaLabel="Got it" />
    </div>
  );
}

function Section({ title, icon, children }) {
  return (<div><div className="flex items-center gap-2 mb-2 text-sm font-semibold"><Icon name={icon} size={16} className="text-gray-500" /> {title}</div><div className="space-y-2">{children}</div></div>);
}
